import { useState } from 'react';
import { ArrowRight, RefreshCw, TriangleAlert, Webhook } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TabsContent } from '@/components/ui/tabs';
import { Typography } from '@/components/ui/typography';
import { ROUTES } from '@/config/routes';
import { integrationStatusCopy } from '@/components/pms/vendor-settings/status/copy';
import { useNavigationService } from '@/services/navigate-service';

import SyncAssets from '../syncAssets/SyncAssets';
import TabbedModal, { type TabbedModalTab } from '../tabbed-modal/TabbedModal';
import WebhooksTab from './WebhooksTab';
import type { AssetSelection } from '@/types/asset.types';
import type { PausedNoticeActions } from '../pmsNoticeActions';
import type { SyncFailure } from '../failed-asset-sync/syncFailure';

interface SyncModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Sync tab visibility — only renders for properties Connect-sync'd to a PMS.
  // When false the modal opens directly on Webhooks. The button that opens
  // this modal is always rendered (see PropertyAssetManagementPage) so a
  // property with neither config still gets the disabled-state messaging on
  // the Webhooks tab instead of a silent no-op.
  showSyncTab: boolean;
  pmsSource: string | null;
  syncSelection: AssetSelection;
  setSyncFailure: (failure: SyncFailure | null) => void;
  // Overloaded-sounding names — here's the split:
  //   onPmsSyncSuccess  = the Sync tab's direct PMS sync succeeded. Stamps
  //                        lastPmsSyncedAt only; the direct PMS sync only
  //                        uploads never-before-sent photo files, so it does
  //                        NOT clear the unsynced badge.
  //   onSyncSuccess     = the Webhooks tab's webhook push succeeded. This is
  //                        the one that clears the unsynced badge, since the
  //                        webhook is the only channel that delivers caption/
  //                        reorder/hide edits and Connect uploads.
  // onPmsSyncSuccess receives the id of the photoset this modal was actually
  // synced against (this modal's own `photosetId` prop) rather than making
  // the parent re-derive it from live state at callback time — closes a
  // stale-selection window if the user's photoset selection moves on while
  // the modal is open.
  onPmsSyncSuccess: (photosetId?: string) => void;
  photosetId: string | null;
  photosetName: string;
  lastWebhookTriggeredAt: string | null;
  /** Pass-through to WebhooksTab — see its prop. */
  hasWebhookEndpoints: boolean;
  /** Pass-through to SyncAssets — the workspace account's watermark answer. */
  watermarkAvailable: boolean;
  /** Count of VISIBLE (non-hidden) photos the sync will deliver — shown in
      the Sync tab summary and CTA. `syncSelection` itself may contain more
      UUIDs (hidden photos ride along for the backend's delete sweep). */
  photoCount: number;
  // Pass-through to WebhooksTab — fires after a successful webhook send so
  // the parent can clear the unsynced badge. See onPmsSyncSuccess above.
  onSyncSuccess?: () => void;
  /** DV-3345: the page's status read for the workspace's account (spec D15). */
  isPaused: boolean;
  /** DV-3345: which button the paused notice shows (pmsNoticeActions). */
  pausedActions: PausedNoticeActions;
}

const SYNC_TAB: TabbedModalTab = { value: 'sync', label: 'Sync', icon: RefreshCw };
const WEBHOOKS_TAB: TabbedModalTab = { value: 'webhooks', label: 'Webhooks', icon: Webhook };

/**
 * The header's Sync modal: the direct PMS sync and the webhook push. It was
 * "Sync & Share" until DV-3714 moved sharing to its own Share control; the
 * tabs are unchanged.
 */
const SyncModal = ({
  open,
  onOpenChange,
  showSyncTab,
  pmsSource,
  syncSelection,
  setSyncFailure,
  onPmsSyncSuccess,
  photosetId,
  photosetName,
  lastWebhookTriggeredAt,
  hasWebhookEndpoints,
  watermarkAvailable,
  photoCount,
  onSyncSuccess,
  isPaused,
  pausedActions
}: SyncModalProps) => {
  // Only a tab the user picked is state. Until then the tab follows
  // `showSyncTab`, which can turn true after opening: the page's PMS status
  // read no longer blocks the photo load (DV-3345). A picked Sync tab that
  // goes away falls back to Webhooks.
  const [pickedTab, setPickedTab] = useState<string | null>(null);
  const defaultTab = showSyncTab ? 'sync' : 'webhooks';
  const activeTab =
    pickedTab === null || (pickedTab === 'sync' && !showSyncTab)
      ? defaultTab
      : pickedTab;
  const { navigateTo } = useNavigationService();

  const handleClose = () => onOpenChange(false);

  return (
    <TabbedModal
      open={open}
      onOpenChange={onOpenChange}
      title="Sync"
      tabs={showSyncTab ? [SYNC_TAB, WEBHOOKS_TAB] : [WEBHOOKS_TAB]}
      value={activeTab}
      onValueChange={setPickedTab}
      sheetTestId="sync-sheet"
    >
      {showSyncTab && pmsSource && (
        <TabsContent value="sync" className="mt-0 outline-none">
          {isPaused ? (
            <div className="flex flex-col gap-6">
              <Alert variant="warning" role="alert">
                <TriangleAlert className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>{integrationStatusCopy.syncShare.title}</AlertTitle>
                <AlertDescription>
                  {pausedActions.body === 'ask-admin'
                    ? integrationStatusCopy.syncShare.bodyAskAdmin(pmsSource)
                    : integrationStatusCopy.syncShare.body(pmsSource)}
                </AlertDescription>
              </Alert>
              <div className="flex flex-col gap-2 border-t border-border pt-4">
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  {pausedActions.goToIntegrations?.kind === 'navigate' && (
                    <Button
                      type="button"
                      onClick={() => {
                        handleClose();
                        navigateTo(ROUTES.INTEGRATIONS);
                      }}
                    >
                      {integrationStatusCopy.syncShare.goToIntegrations}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                  {pausedActions.goToIntegrations?.kind === 'link' && (
                    <Button asChild>
                      <a
                        href={pausedActions.goToIntegrations.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {integrationStatusCopy.syncShare.goToIntegrations}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </a>
                    </Button>
                  )}
                </div>
                {pausedActions.newTabCaption && (
                  <Typography
                    variant="body-sm"
                    className="text-right text-muted-foreground"
                  >
                    {pausedActions.newTabCaption}
                  </Typography>
                )}
              </div>
            </div>
          ) : (
            <SyncAssets
              setShowSyncModel={onOpenChange}
              setSyncFailure={setSyncFailure}
              selection={syncSelection}
              isSyncingFromAssets
              onSyncSuccess={() => onPmsSyncSuccess(photosetId ?? undefined)}
              pmsSource={pmsSource}
              photoCount={photoCount}
              watermarkAvailable={watermarkAvailable}
            />
          )}
        </TabsContent>
      )}

      <TabsContent value="webhooks" className="mt-0 outline-none">
        <WebhooksTab
          photosetId={photosetId}
          photosetName={photosetName}
          lastWebhookTriggeredAt={lastWebhookTriggeredAt}
          onClose={handleClose}
          onSyncSuccess={onSyncSuccess}
          hasWebhookEndpoints={hasWebhookEndpoints}
        />
      </TabsContent>
    </TabbedModal>
  );
};

export default SyncModal;
